import React, { FC, useState } from 'react';
import { Avatar, CardContent, Grid, IconButton, Typography } from '@material-ui/core';
import Card from '../../../../../../../components/Card';
import { LegacyEmployee } from '../../../../../../../interfaces/api/Employee';
import { MdAdd as AddIcon } from '@react-icons/all-files/md/MdAdd';
import { MdClose as CloseIcon } from '@react-icons/all-files/md/MdClose';
import { Link } from 'react-router-dom';
import { createUseStyles } from '../../../../../../../theming';
import userImage from '../../../../../../../images/user.png';
import AddEmployeeDialog from './components/AddEmployeeDialog';
import ConfirmationDialog from '../../../../../../../components/ConfirmationDialog';

interface Props {
  employees: LegacyEmployee[];
  onUpdateEmployees: (
    employeeList: LegacyEmployee['id'][],
    fieldName: 'employeesToRemove' | 'employeesToAdd'
  ) => Promise<LegacyEmployee[]>;
}

const useStyles = createUseStyles((theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  actionIcon: {
    color: theme.palette.primary.main,
  },
  iconButton: {
    padding: '6px !important',
  },
  link: {},
  employeeContainer: {
    display: 'flex',
    alignItems: 'center',
    '& $link': {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
    },
  },
  employeeInfo: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: 6,
    '&:last-child': {
      color: '#b2b6c3',
    },
  },
}));

const EmployeeConcernedCard: FC<Props> = ({ employees, onUpdateEmployees }) => {
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
  const [employeeIdToDelete, setEmployeeIdToDelete] = useState<number>();
  const classes = useStyles();

  const handleDetachEmployee = async () => {
    if (employeeIdToDelete) {
      await onUpdateEmployees([employeeIdToDelete], 'employeesToRemove');
      setEmployeeIdToDelete(undefined);
    }
  };

  return (
    <>
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <div className={classes.header}>
                <Typography variant='overline' color='primary'>
                  Personnes concernées
                </Typography>

                <IconButton className={classes.iconButton} onClick={() => setIsAddEmployeeModalOpen(true)}>
                  <AddIcon className={classes.actionIcon} />
                </IconButton>
              </div>
            </Grid>

            {employees.map((employee) => (
              <Grid item xs={12} key={employee.id}>
                <div className={classes.employeeContainer}>
                  <Link className={classes.link} to={`/dashboard/user/${employee.user?.id}`}>
                    <Avatar
                      alt={`${employee.firstname} ${employee.lastname}`}
                      src={`/${employee.profileImage}` || userImage}
                    />

                    <div className={classes.employeeInfo}>
                      <Typography variant='subtitle2'>{`${employee.firstname} ${employee.lastname}`}</Typography>
                      <Typography variant='caption'>{`${employee.user?.email || "pas d'email renseigné"}`}</Typography>
                    </div>
                  </Link>

                  <IconButton
                    className={classes.iconButton}
                    size='small'
                    onClick={() => setEmployeeIdToDelete(employee.id)}
                  >
                    <CloseIcon />
                  </IconButton>
                </div>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <AddEmployeeDialog
        open={isAddEmployeeModalOpen}
        onClose={() => setIsAddEmployeeModalOpen(false)}
        onValidate={(options) => onUpdateEmployees(options.employeeSelectedId, 'employeesToAdd')}
      />

      <ConfirmationDialog
        title='Attention'
        confirmationText='Supprimer'
        description='Vous allez supprimer cet employé du rendez-vous. Êtes-vous sûr ?'
        open={!!employeeIdToDelete}
        onClose={() => setEmployeeIdToDelete(undefined)}
        onValidate={handleDetachEmployee}
      />
    </>
  );
};

export default EmployeeConcernedCard;
